// BackendDownOverlay — app-wide branded splash shown when the backend is briefly
// unreachable (a 5xx gateway error mid-deploy, or an unresponsive server). Mounted
// above the router so it covers any authenticated page.
//
// It runs a constant heartbeat against /api/health — not just when something
// already failed — so an outage is noticed within seconds even if the user is
// idle and the app is making no calls of its own. Each probe has a short abort
// timeout so a hung request fails fast instead of waiting out the proxy timeout
// (~2 min). The app stays mounted underneath and resumes once the probe recovers.
import { useEffect, useRef, useState } from "react";
import { subscribeBackendStatus, getBackendDown, setBackendDown } from "../lib/backendStatus";
import RLoaderScreen from "./RLoaderScreen";

const HEARTBEAT_MS = 5000;       // probe cadence while healthy
const HEARTBEAT_DOWN_MS = 3000;  // probe faster while down, for quicker recovery
const PROBE_TIMEOUT_MS = 8000;   // allow slow health under load (DB/Redis checks)
const SHOW_DELAY_MS = 600;       // grace before revealing, so a transient blip doesn't flash
const FAIL_THRESHOLD = 2;        // consecutive hard failures before showing overlay

let probing = false;
let failStreak = 0;

async function probeHealth() {
  if (probing) return;
  probing = true;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch("/api/health", { signal: controller.signal });
    if (res.status >= 500) {
      failStreak += 1;
    } else {
      failStreak = 0;
      setBackendDown(false);
    }
  } catch (err) {
    // Match main.jsx: caller abort = slow probe, not necessarily an outage.
    if (err?.name !== "AbortError") {
      failStreak += 1;
    }
  } finally {
    clearTimeout(timer);
    probing = false;
    if (failStreak >= FAIL_THRESHOLD) {
      setBackendDown(true);
    }
  }
}

export default function BackendDownOverlay() {
  const [down, setDown] = useState(getBackendDown());
  const [visible, setVisible] = useState(false);
  const showTimer = useRef(0);

  const lang = navigator.language?.startsWith("es") ? "es" : "en";
  const label = lang === "es" ? "Cargando" : "Loading";

  useEffect(() => subscribeBackendStatus(setDown), []);

  // Self-scheduling heartbeat — avoids resetting the timer when `down` flips.
  useEffect(() => {
    let cancelled = false;
    let timeoutId = 0;

    const tick = async () => {
      await probeHealth();
      if (cancelled) return;
      const ms = getBackendDown() ? HEARTBEAT_DOWN_MS : HEARTBEAT_MS;
      timeoutId = window.setTimeout(tick, ms);
    };

    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timeoutId);
    };
  }, []);

  // Debounce the reveal: only show once the outage has persisted past the grace.
  useEffect(() => {
    if (!down) {
      setVisible(false);
      return undefined;
    }
    showTimer.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => clearTimeout(showTimer.current);
  }, [down]);

  if (!visible) return null;

  return (
    <RLoaderScreen label={label} variant="trace" bg="navy" speed={0.3} size={100} />
  );
}
